import React, { createContext, useContext, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { PACOCA_API_URL, REFLEXO_API_URL } from "../utils/vars";
import { User } from "../types";
import { errorMessage, isWebView } from "../utils/text";
import { useMessage } from "../components/message/alert/alert-context";
import { isMyLove } from "../utils/auth";
import Cookies from "js-cookie";
import { getToken } from "firebase/messaging";
import { getMessagingInstance } from "../utils/firebase";
import { VAPIDKEY } from "../utils/vars";

// Extend the Window interface to include ReactNativeWebView
declare global {
    interface Window {
        ReactNativeWebView?: {
            postMessage?: (message: string) => void;
        };
    }
}

interface AuthContextType {
    user: User | null;
    loginContext: (token: string, user: User, remember: boolean) => void;
    logout: (redirect?: boolean, logoutApi?: boolean, removeFromSession?: boolean) => void;
    setUser: React.Dispatch<React.SetStateAction<User | null>>;
    fetchUser: () => Promise<void>;
    addOtherAccount: () => void;
    isSuper: (showB?: boolean) => boolean;
    isMaster: (showB?: boolean) => boolean;
    removeUserFromSession: (id_user: number) => void;
    changeUser: (id_user: number) => void;
    isAuthenticated: boolean;
    token: string;
    getSavedUsers: () => User[];
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const location = useLocation(); // Obtém a rota atual
    const [token, setToken] = useState<string>(() => sessionStorage.getItem("token") || Cookies.get('token') || "");
    const navigate = useNavigate();
    const { setMessage } = useMessage();
    const isLocal = window.location.hostname === "localhost" || window.location.port !== "";
    const cookieDomain = isLocal ? undefined : '.pacoca.net';

    const cookieOpts = (expires?: number): Cookies.CookieAttributes => ({
        secure: !isLocal,
        sameSite: 'Strict',
        domain: cookieDomain,
        ...(expires !== undefined ? { expires } : {}),
    });

    const transferToCookiesAndRemoveFromLocalStorage = () => {
        // Verifica se o token e o user existem no localStorage
        const storedToken = localStorage.getItem("token");
        const storedUser = localStorage.getItem("user");
        const savedUsers = localStorage.getItem("saved_users");
        // const defaultUser = localStorage.getItem("default-user");

        if (storedToken) {
            // Armazena o token no cookie com a expiração de 7 dias
            Cookies.set('token', storedToken, cookieOpts(365));
            localStorage.removeItem("token"); // Remove do localStorage
        }

        if (storedUser) {
            // Armazena o usuário no cookie
            Cookies.set('user', storedUser, cookieOpts(365));
            localStorage.removeItem("user"); // Remove do localStorage
        }

        if (savedUsers) {
            // Armazena a lista de usuários salvos no cookie
            Cookies.set('saved_users', savedUsers, cookieOpts(365));
            localStorage.removeItem("saved_users"); // Remove a lista de localStorage
        }

        // if (defaultUser) {
        //     // Armazena a lista de usuários salvos no cookie
        //     Cookies.set('default-user', defaultUser, cookieOpts());
        //     localStorage.removeItem("default-user"); // Remove a lista de localStorage
        // }
    };

    useEffect(() => {
        transferToCookiesAndRemoveFromLocalStorage();
    }, []); // O efeito roda apenas uma vez ao montar o componente



    // Inicializa os estados corretamente
    const [user, setUser] = useState<User | null>(() => {
        const storedUser = sessionStorage.getItem("user") || Cookies.get('user');
        return storedUser && storedUser !== "undefined" ? JSON.parse(storedUser) : null;
    });

    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(
        !!sessionStorage.getItem("token") || !!Cookies.get('token')
    );

    const postTokenToReactNative = (token: string | null) => {
        if (window.ReactNativeWebView && typeof window.ReactNativeWebView.postMessage === 'function') {
            window.ReactNativeWebView.postMessage(JSON.stringify({ authToken: token }));
        }
    };

    useEffect(() => {
        if(user){
            Cookies.set('user', JSON.stringify(user), cookieOpts(365));
        }
    }, [user]);

    useEffect(() => {
        generateAndSendToken();
    }, []);

    useEffect(() => {
        const checkAndSendToken = async () => {
            const pushToken = localStorage.getItem("pushToken");
            const oldPushTokenNew = localStorage.getItem("oldPushTokenNew");
            const oldPushToken = localStorage.getItem("oldPushToken");
            const authToken = localStorage.getItem("authToken");
            const system = localStorage.getItem("system") || "pacoca";

            if (
                (pushToken && 
                authToken && 
                ((pushToken.toLowerCase() != "null" && 
                pushToken.toLowerCase() != "undefined" && 
                authToken.toLowerCase() != "null" && 
                authToken.toLowerCase() != "undefined" && 
                oldPushTokenNew != pushToken) || oldPushToken))
            ) {
                try {
                    await axios.post(`${system == "pacoca" ? PACOCA_API_URL : REFLEXO_API_URL}/users/native-notification/token`, {
                        pushToken,
                    }, {
                        headers: {
                            Authorization: `Bearer ${authToken}`,
                            Accept: "application/json",
                        },
                    });
                    localStorage.setItem("oldPushTokenNew", pushToken ? pushToken : "nada aqui");
                    localStorage.removeItem("pushToken");
                    localStorage.removeItem("oldPushToken");
                    localStorage.removeItem("authToken");
                } catch (err) {
                    console.error("Erro ao enviar token", err);
                }
            }
        };

        // Executa na montagem
        checkAndSendToken();

        // Executa quando receber o evento customizado
        const listener = () => checkAndSendToken();
        window.addEventListener("storageChange", listener);

        return () => {
            window.removeEventListener("storageChange", listener);
        };
    }, []);

    const generateAndSendToken = async () => {
        try {
            if(!isAuthenticated) return;

            // iOS / browsers que não suportam push
            if (!("Notification" in window)) {
                console.log("Push não suportado neste navegador");
                return;
            }
            
            if (window.ReactNativeWebView && typeof window.ReactNativeWebView.postMessage === 'function') {
                return;
            }
            
            const permission = await Notification.requestPermission();

            if (permission !== "granted") {
                console.log("Permissão negada");
                return;
            }

            const messaging = await getMessagingInstance();
            if (!messaging) return;
            
            const pushToken = await getToken(messaging, {
                vapidKey: VAPIDKEY
            });

            if (!pushToken || pushToken == localStorage.getItem("pushToken")) return;

            // envia para o backend
            await axios.post(`${PACOCA_API_URL}/users/native-notification/token`, {
                pushToken: pushToken 
            }, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    Accept: "application/json"
                }
            });

            localStorage.setItem("pushToken", pushToken);
        } catch (error) {
            console.error("Erro ao gerar token:", error);
        }
    };

    const addOtherAccount = () => {
        if (user)
            saveUserToLocalStorage(user, token);
        logout()
        navigate(`/login?forceNewLogin=true`);
    }

    const fetchUser = async () => {
        if (!token) return; // Evita chamadas desnecessárias
        if (location.pathname != "/" && location.pathname != "/livros") return; // Evita chamadas desnecessárias
        try {
            const response = await axios.post(`${PACOCA_API_URL}/user`, null, {
                headers: {
                    Accept: "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });

            Cookies.set('user', JSON.stringify(response.data.user), cookieOpts(365));
            // localStorage.setItem("user", JSON.stringify(response.data.user));

            setUser(response.data.user);
            
            postTokenToReactNative(token)
            
            saveUserToLocalStorage(response.data.user, token)
            if (response.data.warn) { 
                if(response.data.warn.title.includes("anúncio") && (!response.data.warn.type || response.data.warn.type == 'warning')){
                    setMessage(`${response.data.warn.title}`, response.data.warn.type ?? "warning", response.data.warn.text, async () => {
                        try {
                            await axios.post(`${PACOCA_API_URL}/users/warn/mark-as-open/${response.data.warn.id}`, null, {
                                headers: {
                                    Accept: "application/json",
                                    Authorization: `Bearer ${token}`,
                                },
                            });
                        } catch (err: any) {
                            const messageError = errorMessage(err)
                            setMessage(messageError, "error", "Erro ao marcar como lido: ")
                            console.error("Erro ao marcar como lido: ", err);
                            console.error(messageError);
                        }
                    }, response.data.warn.text)
                    return;

                }
                
                setMessage(`${response.data.warn.text}`, response.data.warn.type ?? "warning", response.data.warn.title, async () => {
                    try {
                        await axios.post(`${PACOCA_API_URL}/users/warn/mark-as-open/${response.data.warn.id}`, null, {
                            headers: {
                                Accept: "application/json",
                                Authorization: `Bearer ${token}`,
                            },
                        });
                    } catch (err: any) {
                        const messageError = errorMessage(err)
                        setMessage(messageError, "error", "Erro ao marcar como lido: ")
                        console.error("Erro ao marcar como lido: ", err);
                        console.error(messageError);
                    }
                })
            }
        } catch (err: any) {
            // if (err.response) {
                const status = err?.response?.status;
                const messageError = errorMessage(err)
                setMessage(messageError, "error")
                console.error("Erro ao buscar usuário:", err);

                if ([401, 403].includes(status)) {
                    logout(true, true, true); // Desloga o usuário
                } 

            // } else {
            //     setMessage("Você está sem conexão com a internet", "error");
            // }
        }
    };

    const loginContext = (token: string, user: User, remember: boolean) => {
        if (remember)
            saveUserToLocalStorage(user, token)

        // localStorage.setItem("token", token);
        // localStorage.setItem("user", JSON.stringify(user));
        if (!remember) {
            // Armazena no cookie em vez de localStorage
            Cookies.set('token', token, cookieOpts());
            Cookies.set('user', JSON.stringify(user), cookieOpts());
        } else {
            Cookies.set('token', token, cookieOpts(365));
            Cookies.set('user', JSON.stringify(user), cookieOpts(365));
        }

        setIsAuthenticated(true);
        setUser(user);
        setToken(token);
        postTokenToReactNative(token); // <<< AQUI
    };

    const saveUserToLocalStorage = (newUser: User, token: string) => {
        newUser.token = token;
        // // Obtém os usuários salvos no localStorage
        let savedUsers: User[] = JSON.parse(Cookies.get('saved_users') ?? "[]");
        // // Verifica se o usuário já existe na lista
        const existingUserIndex = savedUsers.findIndex((user: User) => user.id === newUser.id);

        if (existingUserIndex !== -1) {
            // Se o usuário já existir, atualize suas informações
            savedUsers[existingUserIndex] = newUser;
        } else {
            // Se não existir e já houver 3 usuários, remove o primeiro
            if (savedUsers.length >= 5) {
                savedUsers.shift();
            }

            // Adiciona o novo usuário à lista
            savedUsers.push(newUser);
        }

        // Salva a lista atualizada no localStorage
        // localStorage.setItem("saved_users", JSON.stringify(savedUsers));
        Cookies.set('saved_users', JSON.stringify(savedUsers), cookieOpts(365));
    };

    const changeUser = (id_user: number) => {
        // Obtém todos os usuários do localStorage
        const savedUsers: User[] = JSON.parse(Cookies.get('saved_users') || "[]");

        // Busca o usuário pelo ID
        const selectedUser = savedUsers.find((u) => u.id === id_user);

        // Se encontrar, atualiza o estado
        if (selectedUser) {
            Cookies.set('token', selectedUser.token, cookieOpts(365));
            Cookies.set('user', JSON.stringify(selectedUser), cookieOpts(365));
            // localStorage.setItem("token", selectedUser.token);
            // localStorage.setItem("user", JSON.stringify(selectedUser));
            setIsAuthenticated(true);
            setUser(selectedUser);
            setToken(selectedUser.token);
            postTokenToReactNative(selectedUser.token); // <<< AQUI
        }

        window.location.reload()
    };

    const removeUserFromSession = async (id_user: number) => {
        // Obtém a lista de usuários salvos no localStorage
        let savedUsers: User[] = JSON.parse(Cookies.get('saved_users') ?? "[]");

        // Encontra o índice do usuário a ser removido
        const userIndex = savedUsers.findIndex((user: User) => user.id === id_user);

        let token = null;
        // Se o usuário for encontrado, removemos da lista
        if (userIndex !== -1) {
            token = savedUsers[userIndex].token;
            savedUsers.splice(userIndex, 1); // Remove o usuário da lista
        }

        // Salva a lista atualizada no localStorage
        // localStorage.setItem("saved_users", JSON.stringify(savedUsers));
        Cookies.set('saved_users', JSON.stringify(savedUsers), cookieOpts(365));

        // remove dados local
        localStorage.removeItem(`chats-${id_user}`);
        localStorage.removeItem(`messagesByChat-${id_user}`);
        localStorage.removeItem(`myNotifications-all-${id_user}`);
        localStorage.removeItem(`myNotifications-mention-${id_user}`);
        localStorage.removeItem(`myNotifications-comment-${id_user}`);
        localStorage.removeItem(`myNotifications-likePost-${id_user}`);
        localStorage.removeItem(`myNotifications-likeComment-${id_user}`);
        localStorage.removeItem(`myNotifications-responseComment-${id_user}`);
        localStorage.removeItem(`myNotifications-resrepostPost-${id_user}`);
        localStorage.removeItem(`default-group-${id_user}`);
        localStorage.removeItem(`default-user-${id_user}`);
        localStorage.removeItem(`totalNotification-${id_user}`);
        localStorage.removeItem(`totalNotificationChat-${id_user}`);
        localStorage.removeItem("oldPushTokenNew");

        await axios.post(`${PACOCA_API_URL}/logout`, {
            token: token,
            user_id: id_user
        }, {
            headers: {
                Accept: "application/json",
                Authorization: `Bearer ${token}`,
            },
        });

    };

    const logout = async (redirect: boolean = false, logoutApi: boolean = false, removeFromSession: boolean = false) => {
        if (removeFromSession) {
            removeUserFromSession(Number(user?.id))
        }

        Cookies.remove('token', { domain: cookieDomain });
        Cookies.remove('user', { domain: cookieDomain });
        // localStorage.removeItem("token");
        // localStorage.removeItem("user");
        sessionStorage.removeItem("token");
        sessionStorage.removeItem("user");
        localStorage.removeItem("oldPushTokenNew");
        setIsAuthenticated(false); // Corrigido aqui
        setUser(null);
        setToken("");
        postTokenToReactNative(null);

        if (logoutApi && !removeFromSession) {
            try {
                axios.post(`${PACOCA_API_URL}/logout`, {
                    token: user?.token,
                    user_id: user?.id
                }, {
                    headers: {
                        Accept: "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                });

            } catch (err) {
                console.error("erro ao sair da conta", err);

            }
        }

        if (redirect) {
            navigate(`/login`)
        }
    };
    const SUPER_IDS = [1];
    const MASTER_IDS = [2, 3, 5, 10];

    const isSuper = (showB = false): boolean => {
        if (!isAuthenticated || !user) return false;

        return (
            SUPER_IDS.includes(user.id) ||
            (showB && user.id === 446)
        );
    };

    const isMaster = (showB = false): boolean => {
        if (!isAuthenticated || !user) return false;

        return (
            MASTER_IDS.includes(user.id) ||
            isSuper(showB)
        );
    };

    useEffect(() => {
        if (token) {
            fetchUser(); // Apenas busca usuário se o token estiver definido
        }
    }, [token]);

    useEffect(() => {
        if (isAuthenticated) {
            fetchUser(); // Revalida ao mudar de página
        }
    }, [location.pathname]);

    const getSavedUsers = (): User[] => {
        return JSON.parse(Cookies.get('saved_users') ?? "[]");
    };

    return (

        <AuthContext.Provider value={{ getSavedUsers, user, token, setUser, isSuper, isMaster, fetchUser, isAuthenticated, loginContext, logout, changeUser, addOtherAccount, removeUserFromSession }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth deve ser usado dentro de um AuthProvider");
    }
    return context;
};
